import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import AdminLayout from './AdminLayout';
import RichTextEditor from './RichTextEditor';
import { api } from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);

  // Total Patients Directory state
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [selectedPatientHistory, setSelectedPatientHistory] = useState(null);

  // Appointments state
  const [appointments, setAppointments] = useState([]);
  const [appointmentFilter, setAppointmentFilter] = useState('all');
  const [appointmentSearch, setAppointmentSearch] = useState('');
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactFilter, setContactFilter] = useState('all');
  const [contactsLoading, setContactsLoading] = useState(false);

  // Articles state
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    specialist: 'Orthopedic Surgeon',
    author: 'Dr. Sunil Apsingi',
    content: '',
  });
  const [articleFile, setArticleFile] = useState(null);
  const [articlePreview, setArticlePreview] = useState(null);

  // Gallery state
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [galleryForm, setGalleryForm] = useState({
    title: '',
    content_type: 'photo',
    youtube_link: '',
  });
  const [galleryFile, setGalleryFile] = useState(null);
  const [galleryPreview, setGalleryPreview] = useState(null);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [replyDrafts, setReplyDrafts] = useState({});

  // Recycle Bin / Trash state
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashFilter, setTrashFilter] = useState('all');
  const [trashSearch, setTrashSearch] = useState('');

  // Feedback Notification Banner
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Fetch Dashboard Summary Stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await api.getAdminStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load admin stats:', err);
    }
  }, []);

  // Fetch Patients Directory
  const fetchPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const params = {};
      if (patientSearch.trim()) params.search = patientSearch.trim();
      const data = await api.getAdminPatients(params);
      setPatients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load patient directory:', err);
    } finally {
      setPatientsLoading(false);
    }
  }, [patientSearch]);

  // Fetch Appointments
  const fetchAppointments = useCallback(async () => {
    setAppointmentsLoading(true);
    try {
      const params = {};
      if (appointmentFilter !== 'all') params.status = appointmentFilter;
      if (appointmentSearch.trim()) params.search = appointmentSearch.trim();
      const data = await api.getAdminAppointments(params);
      setAppointments(data?.results ? data.results : (Array.isArray(data) ? data : []));
    } catch (err) {
      console.error('Failed to load appointments:', err);
    } finally {
      setAppointmentsLoading(false);
    }
  }, [appointmentFilter, appointmentSearch]);

  // Fetch Contacts
  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const params = {};
      if (contactFilter !== 'all') params.status = contactFilter;
      const data = await api.getAdminContacts(params);
      setContacts(data?.results ? data.results : (Array.isArray(data) ? data : []));
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  }, [contactFilter]);

  // Fetch Articles
  const fetchArticles = useCallback(async () => {
    setArticlesLoading(true);
    try {
      const data = await api.getArticles();
      setArticles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load articles:', err);
    } finally {
      setArticlesLoading(false);
    }
  }, []);

  // Fetch Gallery
  const fetchGallery = useCallback(async () => {
    setGalleryLoading(true);
    try {
      const data = await api.getGallery();
      setGalleryItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load gallery:', err);
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  // Fetch Reviews
  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const data = await api.getAdminReviews();
      const list = data?.results ? data.results : (Array.isArray(data) ? data : []);
      setReviews(list);
      const drafts = {};
      list.forEach((r) => {
        drafts[r.id] = r.owner_reply || '';
      });
      setReplyDrafts(drafts);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  // Fetch Recycle Bin / Trash
  const fetchTrash = useCallback(async () => {
    setTrashLoading(true);
    try {
      const params = {};
      if (trashFilter !== 'all') params.type = trashFilter;
      if (trashSearch.trim()) params.search = trashSearch.trim();
      const data = await api.getAdminTrash(params);
      setTrashItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load trash items:', err);
    } finally {
      setTrashLoading(false);
    }
  }, [trashFilter, trashSearch]);

  // Initial Load & Tab Changes
  useEffect(() => {
    fetchStats();
    fetchPatients();
    fetchAppointments();
    fetchContacts();
    fetchArticles();
    fetchGallery();
    fetchReviews();
    fetchTrash();
  }, [fetchStats, fetchPatients, fetchAppointments, fetchContacts, fetchArticles, fetchGallery, fetchReviews, fetchTrash]);

  // Excel Export Handlers
  const handleExportPatients = () => {
    const exportData = patients.map((p, idx) => ({
      '#': idx + 1,
      'Patient Name': p.name,
      'Contact Number': p.phone,
      'Total Consultations': p.total_visits,
      'First Consultation': p.first_date,
      'Latest Consultation': p.latest_date,
      'Latest Status': p.latest_status ? p.latest_status.toUpperCase() : 'PENDING',
      'Clinical Conditions & Symptoms': (p.conditions || []).join('; '),
    }));
    exportToExcel(exportData, 'Dr_Sunil_Apsingi_Patient_Directory', 'Patients');
    showNotification('Patient Directory exported to Excel successfully!');
  };

  const handleExportAppointments = () => {
    const exportData = appointments.map((a) => ({
      'Appointment ID': a.id,
      'Patient Name': a.patient_name,
      'Contact Number': a.contact_number,
      'Appointment Date': a.appointment_date,
      'Time Slot': a.appointment_time,
      'Primary Condition / Reason': a.reason,
      'Status': a.status ? a.status.toUpperCase() : 'PENDING',
      'Booked Timestamp': a.created_at ? format(new Date(a.created_at), 'yyyy-MM-dd HH:mm') : '',
    }));
    exportToExcel(exportData, 'Dr_Sunil_Apsingi_Appointments', 'Appointments');
    showNotification('Appointments exported to Excel successfully!');
  };

  const handleExportContacts = () => {
    const exportData = contacts.map((c) => ({
      'Inquiry ID': c.id,
      'Patient Name': c.name,
      'Email Address': c.email,
      'Inquiry Message': c.message,
      'Status': c.status ? c.status.toUpperCase() : 'UNREAD',
      'Received At': c.created_at ? format(new Date(c.created_at), 'yyyy-MM-dd HH:mm') : '',
    }));
    exportToExcel(exportData, 'Dr_Sunil_Apsingi_Inquiries', 'Inquiries');
    showNotification('Patient Inquiries exported to Excel successfully!');
  };

  const handleExportArticles = () => {
    const exportData = articles.map((art) => ({
      'Article ID': art.id,
      'Title': art.title,
      'Specialist Field': art.specialist,
      'Author': art.author,
      'Slug': art.slug || '',
      'Published Date': art.datetime ? format(new Date(art.datetime), 'yyyy-MM-dd') : '',
    }));
    exportToExcel(exportData, 'Dr_Sunil_Apsingi_Articles', 'Articles');
    showNotification('Articles exported to Excel successfully!');
  };

  const handleExportReviews = () => {
    const exportData = reviews.map((r) => ({
      'Review ID': r.id,
      'Patient Name': r.author_name,
      'Rating': `${r.rating} / 5`,
      'Review Content': r.review_text,
      'Featured on Website': r.is_featured ? 'YES' : 'NO',
      'Doctor Reply': r.owner_reply || 'None',
      'Recorded Date': r.created_at ? format(new Date(r.created_at), 'yyyy-MM-dd') : '',
    }));
    exportToExcel(exportData, 'Dr_Sunil_Apsingi_Reviews', 'Reviews');
    showNotification('Reviews exported to Excel successfully!');
  };

  const handleExportTrash = () => {
    const exportData = trashItems.map((item, idx) => ({
      '#': idx + 1,
      'Item Type': item.type_label,
      'Title / Description': item.title,
      'Details / Snippet': item.details,
      'Deleted Timestamp': item.deleted_at ? format(new Date(item.deleted_at), 'yyyy-MM-dd HH:mm') : 'N/A',
    }));
    exportToExcel(exportData, 'Dr_Sunil_Apsingi_Recycle_Bin', 'Recycle Bin');
    showNotification('Recycle Bin exported to Excel successfully!');
  };

  // Appointment Status Actions
  const handleUpdateAppointmentStatus = async (id, newStatus) => {
    try {
      await api.updateAppointment(id, { status: newStatus });
      showNotification(`Appointment marked as ${newStatus.toUpperCase()}`);
      fetchAppointments();
      fetchPatients();
      fetchStats();
    } catch (err) {
      showNotification(err.message || 'Failed to update appointment status', 'danger');
    }
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('Move this appointment to Recycle Bin? You can restore it at any time.')) return;
    try {
      await api.deleteAppointment(id);
      showNotification('Appointment safely moved to Recycle Bin');
      fetchAppointments();
      fetchPatients();
      fetchStats();
      fetchTrash();
    } catch (err) {
      showNotification(err.message || 'Failed to delete appointment', 'danger');
    }
  };

  // Contact Status Actions
  const handleUpdateContactStatus = async (id, newStatus) => {
    try {
      await api.updateContact(id, { status: newStatus });
      showNotification(`Message marked as ${newStatus}`);
      fetchContacts();
      fetchStats();
    } catch (err) {
      showNotification(err.message || 'Failed to update status', 'danger');
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Move this inquiry to Recycle Bin? You can restore it at any time.')) return;
    try {
      await api.deleteContact(id);
      showNotification('Inquiry safely moved to Recycle Bin');
      fetchContacts();
      fetchStats();
      fetchTrash();
    } catch (err) {
      showNotification(err.message || 'Failed to delete inquiry', 'danger');
    }
  };

  // Article File & Form Actions
  const handleArticleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArticleFile(file);
      setArticlePreview(URL.createObjectURL(file));
    }
  };

  const handleSaveArticle = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', articleForm.title.trim());
      formData.append('specialist', articleForm.specialist.trim());
      formData.append('author', articleForm.author.trim());
      formData.append('content', articleForm.content.trim());
      if (articleFile) {
        formData.append('poster', articleFile);
      }

      if (editingArticle) {
        await api.updateArticle(editingArticle.id, formData);
        showNotification('Article updated successfully!');
      } else {
        await api.createArticle(formData);
        showNotification('New article published successfully!');
      }

      setArticleModalOpen(false);
      setEditingArticle(null);
      setArticleFile(null);
      setArticlePreview(null);
      setArticleForm({
        title: '',
        specialist: 'Orthopedic Surgeon',
        author: 'Dr. Sunil Apsingi',
        content: '',
      });
      fetchArticles();
      fetchStats();
      fetchTrash();
    } catch (err) {
      showNotification(err.message || 'Failed to save article', 'danger');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Move this article to Recycle Bin? You can restore it at any time.')) return;
    try {
      await api.deleteArticle(id);
      showNotification('Article safely moved to Recycle Bin');
      fetchArticles();
      fetchStats();
      fetchTrash();
    } catch (err) {
      showNotification(err.message || 'Failed to delete article', 'danger');
    }
  };

  // Gallery File & Form Actions
  const handleGalleryFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setGalleryFile(file);
      setGalleryPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveGallery = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', galleryForm.title.trim());
      formData.append('content_type', galleryForm.content_type);

      if (galleryForm.content_type === 'photo') {
        if (!galleryFile) {
          showNotification('Please select an image file to upload.', 'danger');
          return;
        }
        formData.append('photo', galleryFile);
      } else {
        if (!galleryForm.youtube_link.trim()) {
          showNotification('Please enter a valid YouTube link.', 'danger');
          return;
        }
        formData.append('youtube_link', galleryForm.youtube_link.trim());
      }

      await api.createGalleryItem(formData);
      showNotification('Media added to surgical gallery!');
      setGalleryModalOpen(false);
      setGalleryFile(null);
      setGalleryPreview(null);
      setGalleryForm({ title: '', content_type: 'photo', youtube_link: '' });
      fetchGallery();
      fetchStats();
      fetchTrash();
    } catch (err) {
      showNotification(err.message || 'Failed to upload gallery media', 'danger');
    }
  };

  const handleDeleteGallery = async (id) => {
    if (!window.confirm('Move this gallery media to Recycle Bin? You can restore it at any time.')) return;
    try {
      await api.deleteGalleryItem(id);
      showNotification('Gallery media safely moved to Recycle Bin');
      fetchGallery();
      fetchStats();
      fetchTrash();
    } catch (err) {
      showNotification(err.message || 'Failed to delete media', 'danger');
    }
  };

  // Review Actions
  const handleToggleReviewFeatured = async (review) => {
    try {
      await api.updateReview(review.id, { is_featured: !review.is_featured });
      showNotification(
        `Review is now ${!review.is_featured ? 'Featured on Website' : 'Hidden from Website'}`
      );
      fetchReviews();
    } catch (err) {
      showNotification(err.message || 'Failed to update review', 'danger');
    }
  };

  const handleSaveReviewReply = async (reviewId) => {
    try {
      await api.updateReview(reviewId, { owner_reply: replyDrafts[reviewId] || '' });
      showNotification("Doctor's response saved!");
      fetchReviews();
    } catch (err) {
      showNotification(err.message || 'Failed to save reply', 'danger');
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Move this review to Recycle Bin? You can restore it at any time.')) return;
    try {
      await api.deleteReview(id);
      showNotification('Review safely moved to Recycle Bin');
      fetchReviews();
      fetchStats();
      fetchTrash();
    } catch (err) {
      showNotification(err.message || 'Failed to delete review', 'danger');
    }
  };

  // Recycle Bin / Trash Actions
  const handleRestoreTrashItem = async (item) => {
    try {
      await api.restoreAdminTrashItem(item.type, item.id);
      showNotification(`"${item.title || item.type_label}" restored successfully!`);
      fetchTrash();
      fetchStats();
      if (item.type === 'appointment') {
        fetchAppointments();
        fetchPatients();
      } else if (item.type === 'contact') {
        fetchContacts();
      } else if (item.type === 'article') {
        fetchArticles();
      } else if (item.type === 'gallery') {
        fetchGallery();
      } else if (item.type === 'review') {
        fetchReviews();
      }
    } catch (err) {
      showNotification(err.message || 'Failed to restore item', 'danger');
    }
  };

  const handlePurgeTrashItem = async (item) => {
    if (!window.confirm(`Permanently destroy "${item.title || item.type_label}"? This action CANNOT be undone.`)) return;
    try {
      await api.purgeAdminTrashItem(item.type, item.id);
      showNotification(`"${item.title || item.type_label}" permanently destroyed.`);
      fetchTrash();
      fetchStats();
    } catch (err) {
      showNotification(err.message || 'Failed to permanently delete item', 'danger');
    }
  };

  const badgeCounts = {
    totalPatients: stats?.patients?.total || patients.length || 0,
    pendingAppointments: stats?.appointments?.pending || 0,
    unreadContacts: stats?.contacts?.unread || 0,
    totalTrash: stats?.trash?.total || 0,
  };

  return (
    <AdminLayout
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      counts={badgeCounts}
    >
      {/* Toast Notification */}
      {notification && (
        <div className={`alert alert-${notification.type} alert-floating shadow-lg d-flex align-items-center gap-2`}>
          <i
            className={`fa-solid ${
              notification.type === 'danger' ? 'fa-circle-xmark' : 'fa-circle-check'
            }`}
          ></i>
          <span>{notification.message}</span>
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 1: OVERVIEW */}
      {/* ==================================================================== */}
      {activeTab === 'overview' && (
        <div className="tab-pane-overview">
          {/* Welcome Banner */}
          <div className="overview-welcome-card p-4 rounded-4 mb-4 text-white">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <span className="badge-welcome mb-2 d-inline-block">
                  <i className="fa-solid fa-notes-medical me-1"></i> Operations Command
                </span>
                <h2 className="welcome-title mb-1">Welcome back, Dr. Sunil Apsingi</h2>
                <p className="welcome-desc mb-0 text-white-50">
                  Medicover Out Patient Center • HITEC City, Hyderabad. Review patient records, appointments, and hospital inquiries.
                </p>
              </div>
              <div className="col-lg-4 text-lg-end mt-3 mt-lg-0 d-flex flex-wrap justify-content-lg-end gap-2">
                <button
                  className="btn btn-excel-export"
                  onClick={handleExportAppointments}
                >
                  <i className="fa-solid fa-file-excel me-1 text-success"></i> Export Today's List
                </button>
                <button
                  className="btn btn-refresh-stats"
                  onClick={() => {
                    fetchStats();
                    fetchPatients();
                    fetchAppointments();
                    fetchContacts();
                    showNotification('Dashboard refreshed!');
                  }}
                >
                  <i className="fa-solid fa-rotate me-1"></i> Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Metric Cards Grid */}
          <div className="row g-3 mb-4">
            <div className="col-xl-3 col-sm-6">
              <div
                className="metric-card h-100 cursor-pointer"
                onClick={() => setActiveTab('patients')}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="metric-label">Total Patients</span>
                  <div className="metric-icon-bubble bg-teal-soft text-teal">
                    <i className="fa-solid fa-hospital-user"></i>
                  </div>
                </div>
                <h2 className="metric-value mb-1 text-teal">{stats?.patients?.total ?? patients.length}</h2>
                <span className="metric-meta text-muted small">Unique registered patients</span>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6">
              <div
                className="metric-card h-100 cursor-pointer"
                onClick={() => {
                  setAppointmentFilter('pending');
                  setActiveTab('appointments');
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="metric-label">Pending Requests</span>
                  <div className="metric-icon-bubble bg-amber-soft text-amber">
                    <i className="fa-solid fa-hourglass-half"></i>
                  </div>
                </div>
                <h2 className="metric-value mb-1 text-amber">
                  {stats?.appointments?.pending ?? '0'}
                </h2>
                <span className="metric-meta text-muted small">Awaiting slot confirmation</span>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6">
              <div
                className="metric-card h-100 cursor-pointer"
                onClick={() => {
                  setContactFilter('unread');
                  setActiveTab('contacts');
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="metric-label">Unread Messages</span>
                  <div className="metric-icon-bubble bg-orange-soft text-orange">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                </div>
                <h2 className="metric-value mb-1 text-orange">
                  {stats?.contacts?.unread ?? '0'}
                </h2>
                <span className="metric-meta text-muted small">New website patient inquiries</span>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6">
              <div
                className="metric-card h-100 cursor-pointer"
                onClick={() => setActiveTab('articles')}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="metric-label">Published Articles</span>
                  <div className="metric-icon-bubble bg-indigo-soft text-indigo">
                    <i className="fa-solid fa-book-medical"></i>
                  </div>
                </div>
                <h2 className="metric-value mb-1">{stats?.articles?.total ?? '0'}</h2>
                <span className="metric-meta text-muted small">Live clinical guides</span>
              </div>
            </div>
          </div>

          {/* Quick Tables Grid */}
          <div className="row g-4">
            <div className="col-lg-7">
              <div className="dashboard-section-card h-100 p-4 rounded-4 bg-white shadow-sm">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="section-card-title mb-0">
                    <i className="fa-solid fa-calendar-check text-orange me-2"></i> Recent Consultation Requests
                  </h5>
                  <button
                    className="btn btn-link btn-sm text-decoration-none text-orange fw-semibold"
                    onClick={() => setActiveTab('appointments')}
                  >
                    View All ({stats?.appointments?.total ?? '0'}) →
                  </button>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr className="small text-muted">
                        <th>Patient</th>
                        <th>Preferred Date</th>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.slice(0, 5).map((app) => (
                        <tr key={app.id}>
                          <td>
                            <strong className="d-block text-navy">{app.patient_name}</strong>
                            <small className="text-muted text-truncate d-inline-block" style={{ maxWidth: '160px' }}>
                              {app.reason}
                            </small>
                          </td>
                          <td>
                            <span className="d-block small fw-semibold text-navy">
                              {app.appointment_date}
                            </span>
                            <small className="text-muted">{app.appointment_time}</small>
                          </td>
                          <td>
                            <a
                              href={`tel:${app.contact_number}`}
                              className="btn btn-xs btn-outline-teal text-decoration-none"
                            >
                              <i className="fa-solid fa-phone me-1"></i> {app.contact_number}
                            </a>
                          </td>
                          <td>
                            <span className={`status-pill pill-${app.status}`}>
                              {app.status}
                            </span>
                          </td>
                          <td>
                            {app.status === 'pending' ? (
                              <button
                                className="btn btn-sm btn-success-soft py-1 px-2"
                                title="Confirm Appointment"
                                onClick={() => handleUpdateAppointmentStatus(app.id, 'confirmed')}
                              >
                                <i className="fa-solid fa-check"></i>
                              </button>
                            ) : (
                              <span className="text-muted small">Logged</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="dashboard-section-card h-100 p-4 rounded-4 bg-white shadow-sm">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="section-card-title mb-0">
                    <i className="fa-solid fa-envelope-open-text text-teal me-2"></i> Recent Inquiries
                  </h5>
                  <button
                    className="btn btn-link btn-sm text-decoration-none text-teal fw-semibold"
                    onClick={() => setActiveTab('contacts')}
                  >
                    View All ({stats?.contacts?.total ?? '0'}) →
                  </button>
                </div>

                <div className="quick-messages-list">
                  {contacts.slice(0, 4).map((msg) => (
                    <div key={msg.id} className={`quick-message-item p-3 mb-2 rounded-3 ${msg.status === 'unread' ? 'unread-bg' : 'read-bg'}`}>
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <strong className="text-navy small">{msg.name}</strong>
                        <span className={`status-pill pill-${msg.status} font-monospace`} style={{ fontSize: '0.68rem' }}>
                          {msg.status}
                        </span>
                      </div>
                      <p className="small text-secondary mb-2 text-truncate-2">
                        {msg.message}
                      </p>
                      <div className="d-flex justify-content-between align-items-center">
                        <a href={`mailto:${msg.email}`} className="text-teal small text-decoration-none">
                          <i className="fa-regular fa-envelope me-1"></i> {msg.email}
                        </a>
                        {msg.status === 'unread' && (
                          <button
                            className="btn btn-xs btn-outline-secondary"
                            onClick={() => handleUpdateContactStatus(msg.id, 'read')}
                          >
                            Mark Read
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 2: TOTAL PATIENT DETAILS DIRECTORY */}
      {/* ==================================================================== */}
      {activeTab === 'patients' && (
        <div className="tab-pane-patients">
          <div className="filter-toolbar bg-white p-3 rounded-4 shadow-sm mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
            {/* Search Box */}
            <div className="search-box-wrapper flex-grow-1" style={{ maxWidth: '420px' }}>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <i className="fa-solid fa-magnifying-glass text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by patient name, phone number, symptoms..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Export & Actions */}
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-excel-export"
                onClick={handleExportPatients}
                title="Download Patient Directory as Excel Sheet"
              >
                <i className="fa-solid fa-file-excel text-success me-2"></i> Export Patients to Excel
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchPatients}
                disabled={patientsLoading}
              >
                <i className={`fa-solid fa-rotate ${patientsLoading ? 'fa-spin' : ''} me-1`}></i> Refresh
              </button>
            </div>
          </div>

          {/* Patients Directory Table */}
          <div className="bg-white rounded-4 shadow-sm p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-1 text-navy fw-bold">
                  Total Patient Records ({patients.length})
                </h5>
                <small className="text-muted">Master directory compiled from all appointments and clinic visits</small>
              </div>
            </div>

            {patientsLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-teal" role="status"></div>
                <p className="mt-2 text-muted small">Loading patient directory...</p>
              </div>
            ) : patients.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fa-solid fa-user-xmark fs-2 mb-2"></i>
                <p>No patient records found.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr className="small text-muted">
                      <th>#</th>
                      <th>Patient Name</th>
                      <th>Contact Number</th>
                      <th>Total Visits</th>
                      <th>First Visit</th>
                      <th>Latest Visit</th>
                      <th>Recorded Conditions / Symptoms</th>
                      <th className="text-end">Patient History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patients.map((p, idx) => (
                      <tr key={p.id || idx}>
                        <td className="font-monospace text-muted small">{idx + 1}</td>
                        <td>
                          <strong className="text-navy d-block">{p.name}</strong>
                        </td>
                        <td>
                          <a
                            href={`tel:${p.phone}`}
                            className="btn btn-xs btn-outline-teal text-decoration-none"
                          >
                            <i className="fa-solid fa-phone me-1"></i> {p.phone}
                          </a>
                        </td>
                        <td>
                          <span className={`badge ${p.total_visits > 1 ? 'bg-primary-subtle text-primary border border-primary-subtle' : 'bg-light text-secondary'} font-monospace px-2 py-1`}>
                            {p.total_visits} {p.total_visits > 1 ? 'Consultations' : 'Visit'}
                          </span>
                        </td>
                        <td>
                          <span className="small text-muted">{p.first_date || 'N/A'}</span>
                        </td>
                        <td>
                          <span className="small fw-semibold text-navy">{p.latest_date}</span>
                        </td>
                        <td style={{ maxWidth: '280px' }}>
                          <div className="d-flex flex-wrap gap-1">
                            {p.conditions?.map((c, cIdx) => (
                              <span key={cIdx} className="badge bg-light text-dark font-normal small border">
                                {c}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-teal"
                            onClick={() => setSelectedPatientHistory(p)}
                          >
                            <i className="fa-solid fa-clock-rotate-left me-1"></i> View History
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Patient History Modal */}
          {selectedPatientHistory && (
            <div className="custom-modal-backdrop">
              <div className="custom-modal-dialog">
                <div className="modal-content rounded-4 p-4 shadow-lg bg-white">
                  <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                    <div>
                      <h5 className="modal-title text-navy fw-bold mb-0">
                        {selectedPatientHistory.name} - Complete Medical History
                      </h5>
                      <small className="text-teal font-monospace">
                        <i className="fa-solid fa-phone me-1"></i> {selectedPatientHistory.phone}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSelectedPatientHistory(null)}
                    ></button>
                  </div>

                  <div className="patient-timeline-list py-2">
                    <h6 className="small text-muted fw-bold mb-3 uppercase">Past Consultations & Appointments</h6>
                    {selectedPatientHistory.history?.map((h, hIdx) => (
                      <div key={hIdx} className="timeline-item p-3 mb-2 rounded-3 bg-light border">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong className="text-navy small">
                            <i className="fa-regular fa-calendar me-1 text-teal"></i> {h.date} at {h.time}
                          </strong>
                          <span className={`status-pill pill-${h.status}`}>
                            {h.status}
                          </span>
                        </div>
                        <p className="small text-secondary mb-0">
                          <strong>Reason / Symptoms:</strong> {h.reason || 'General joint evaluation'}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                    <a
                      href={`tel:${selectedPatientHistory.phone}`}
                      className="btn btn-sm btn-medical-primary"
                    >
                      <i className="fa-solid fa-phone me-1"></i> Call Patient
                    </a>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => setSelectedPatientHistory(null)}
                    >
                      Close History
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 3: APPOINTMENTS DESK */}
      {/* ==================================================================== */}
      {activeTab === 'appointments' && (
        <div className="tab-pane-appointments">
          <div className="filter-toolbar bg-white p-3 rounded-4 shadow-sm mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
            {/* Status Tabs */}
            <div className="status-filter-group d-flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All Requests' },
                { key: 'pending', label: 'Pending' },
                { key: 'confirmed', label: 'Confirmed' },
                { key: 'completed', label: 'Completed' },
                { key: 'cancelled', label: 'Cancelled' },
              ].map((f) => (
                <button
                  key={f.key}
                  className={`btn btn-filter-pill ${appointmentFilter === f.key ? 'active' : ''}`}
                  onClick={() => setAppointmentFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Search Box & Excel Export */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="search-box-wrapper">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search patient, phone..."
                  value={appointmentSearch}
                  onChange={(e) => setAppointmentSearch(e.target.value)}
                />
              </div>
              <button
                className="btn btn-excel-export"
                onClick={handleExportAppointments}
                title="Download Appointments as Excel Sheet"
              >
                <i className="fa-solid fa-file-excel text-success me-1"></i> Export to Excel
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchAppointments}
                disabled={appointmentsLoading}
              >
                <i className={`fa-solid fa-rotate ${appointmentsLoading ? 'fa-spin' : ''}`}></i>
              </button>
            </div>
          </div>

          {/* Appointments Table */}
          <div className="bg-white rounded-4 shadow-sm p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0 text-navy fw-bold">
                Consultation Appointments ({appointments.length})
              </h5>
            </div>

            {appointmentsLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-orange" role="status"></div>
                <p className="mt-2 text-muted small">Loading appointments...</p>
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fa-regular fa-calendar-xmark fs-2 mb-2 text-muted"></i>
                <p>No appointments match the selected filter.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr className="small text-muted">
                      <th>ID</th>
                      <th>Patient Details</th>
                      <th>Date & Slot</th>
                      <th>Symptoms / Notes</th>
                      <th>Status</th>
                      <th className="text-end">Manage Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.map((app) => (
                      <tr key={app.id}>
                        <td className="font-monospace text-muted small">#{app.id}</td>
                        <td>
                          <div className="fw-bold text-navy">{app.patient_name}</div>
                          <a
                            href={`tel:${app.contact_number}`}
                            className="text-decoration-none text-teal small fw-semibold"
                          >
                            <i className="fa-solid fa-phone me-1"></i> {app.contact_number}
                          </a>
                        </td>
                        <td>
                          <div className="fw-semibold text-navy small">
                            {app.appointment_date}
                          </div>
                          <span className="badge bg-light text-dark font-monospace" style={{ fontSize: '0.72rem' }}>
                            {app.appointment_time}
                          </span>
                        </td>
                        <td style={{ maxWidth: '280px' }}>
                          <p className="mb-0 small text-secondary lh-sm">
                            {app.reason || 'General orthopedic consultation'}
                          </p>
                        </td>
                        <td>
                          <span className={`status-pill pill-${app.status}`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="d-inline-flex gap-1">
                            {app.status !== 'confirmed' && (
                              <button
                                className="btn btn-sm btn-success-soft py-1 px-2"
                                title="Mark Confirmed"
                                onClick={() => handleUpdateAppointmentStatus(app.id, 'confirmed')}
                              >
                                <i className="fa-solid fa-check"></i>
                              </button>
                            )}
                            {app.status !== 'completed' && (
                              <button
                                className="btn btn-sm btn-blue-soft py-1 px-2"
                                title="Mark Completed"
                                onClick={() => handleUpdateAppointmentStatus(app.id, 'completed')}
                              >
                                <i className="fa-solid fa-check-double"></i>
                              </button>
                            )}
                            {app.status !== 'cancelled' && (
                              <button
                                className="btn btn-sm btn-amber-soft py-1 px-2"
                                title="Cancel Appointment"
                                onClick={() => handleUpdateAppointmentStatus(app.id, 'cancelled')}
                              >
                                <i className="fa-solid fa-ban"></i>
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-danger-soft py-1 px-2"
                              title="Delete Record"
                              onClick={() => handleDeleteAppointment(app.id)}
                            >
                              <i className="fa-regular fa-trash-can"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 4: PATIENT INQUIRIES DESK */}
      {/* ==================================================================== */}
      {activeTab === 'contacts' && (
        <div className="tab-pane-contacts">
          <div className="filter-toolbar bg-white p-3 rounded-4 shadow-sm mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="status-filter-group d-flex gap-2">
              {[
                { key: 'all', label: 'All Inquiries' },
                { key: 'unread', label: 'Unread' },
                { key: 'read', label: 'Read' },
                { key: 'responded', label: 'Responded' },
              ].map((f) => (
                <button
                  key={f.key}
                  className={`btn btn-filter-pill ${contactFilter === f.key ? 'active' : ''}`}
                  onClick={() => setContactFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-excel-export"
                onClick={handleExportContacts}
                title="Download Inquiries as Excel Sheet"
              >
                <i className="fa-solid fa-file-excel text-success me-1"></i> Export to Excel
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchContacts}
                disabled={contactsLoading}
              >
                <i className={`fa-solid fa-rotate ${contactsLoading ? 'fa-spin' : ''}`}></i>
              </button>
            </div>
          </div>

          <div className="row g-3">
            {contacts.map((msg) => (
              <div key={msg.id} className="col-lg-6">
                <div className={`contact-card p-4 rounded-4 shadow-sm bg-white h-100 border-start border-4 ${msg.status === 'unread' ? 'border-warning' : 'border-success'}`}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 className="mb-0 text-navy fw-bold">{msg.name}</h5>
                      <a href={`mailto:${msg.email}`} className="text-teal small text-decoration-none">
                        <i className="fa-regular fa-envelope me-1"></i> {msg.email}
                      </a>
                    </div>
                    <span className={`status-pill pill-${msg.status}`}>
                      {msg.status}
                    </span>
                  </div>

                  <div className="inquiry-message-box p-3 rounded-3 bg-light my-3">
                    <p className="mb-0 small text-secondary lh-base">
                      {msg.message}
                    </p>
                  </div>

                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <small className="text-muted">
                      {msg.created_at ? format(new Date(msg.created_at), 'MMM d, yyyy h:mm a') : 'Recent'}
                    </small>
                    <div className="d-flex gap-2">
                      {msg.status !== 'responded' && (
                        <button
                          className="btn btn-xs btn-outline-teal"
                          onClick={() => handleUpdateContactStatus(msg.id, 'responded')}
                        >
                          <i className="fa-solid fa-reply me-1"></i> Mark Responded
                        </button>
                      )}
                      <a
                        href={`mailto:${msg.email}?subject=Dr. Sunil Apsingi Clinic Consultation Reply`}
                        className="btn btn-xs btn-medical-primary"
                      >
                        Reply via Email
                      </a>
                      <button
                        className="btn btn-xs btn-outline-danger"
                        onClick={() => handleDeleteContact(msg.id)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {contacts.length === 0 && (
              <div className="col-12 text-center py-5 text-muted bg-white rounded-4 shadow-sm">
                No patient inquiry messages matching filter.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 5: ARTICLES & BLOGS */}
      {/* ==================================================================== */}
      {activeTab === 'articles' && (
        <div className="tab-pane-articles">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div>
              <h4 className="mb-1 text-navy fw-bold">Clinical Articles & Guides</h4>
              <p className="text-muted small mb-0">Publish and manage orthopedic education articles</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-excel-export"
                onClick={handleExportArticles}
                title="Download Articles Catalog as Excel"
              >
                <i className="fa-solid fa-file-excel text-success me-1"></i> Export to Excel
              </button>
              <button
                className="btn btn-admin-primary d-flex align-items-center gap-2"
                onClick={() => {
                  setEditingArticle(null);
                  setArticleFile(null);
                  setArticlePreview(null);
                  setArticleForm({
                    title: '',
                    specialist: 'Orthopedic Surgeon',
                    author: 'Dr. Sunil Apsingi',
                    content: '',
                  });
                  setArticleModalOpen(true);
                }}
              >
                <i className="fa-solid fa-plus"></i>
                <span>Publish New Article</span>
              </button>
            </div>
          </div>

          {articlesLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-orange" role="status"></div>
              <p className="mt-2 text-muted small">Loading articles...</p>
            </div>
          ) : (
            <div className="row g-4">
              {articles.map((art) => (
                <div key={art.id} className="col-lg-4 col-md-6">
                  <div className="article-admin-card bg-white rounded-4 shadow-sm h-100 overflow-hidden d-flex flex-column">
                    <div className="art-poster-wrapper position-relative">
                      <img
                        src={art.poster || '/images/image-(1).png'}
                        alt={art.title}
                        className="art-poster-img"
                      />
                      <span className="art-specialty-badge">
                        {art.specialist || 'Orthopedic Care'}
                      </span>
                    </div>

                    <div className="p-4 flex-grow-1 d-flex flex-column">
                      <h5 className="art-card-title mb-2 text-navy fw-bold">{art.title}</h5>
                      <small className="text-muted mb-3 d-block">
                        By {art.author || 'Dr. Sunil Apsingi'}
                      </small>

                      <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center">
                        <a
                          href={`/blogs/${art.slug || art.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-xs btn-outline-secondary"
                        >
                          <i className="fa-solid fa-eye me-1"></i> View Live
                        </a>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-xs btn-outline-teal"
                            onClick={() => {
                              setEditingArticle(art);
                              setArticleFile(null);
                              setArticlePreview(art.poster || null);
                              setArticleForm({
                                title: art.title || '',
                                specialist: art.specialist || 'Orthopedic Surgeon',
                                author: art.author || 'Dr. Sunil Apsingi',
                                content: art.content || '',
                              });
                              setArticleModalOpen(true);
                            }}
                          >
                            <i className="fa-solid fa-pen-to-square me-1"></i> Edit
                          </button>
                          <button
                            className="btn btn-xs btn-outline-danger"
                            onClick={() => handleDeleteArticle(art.id)}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Article Modal with Image File Upload & Word-style Rich Text Editor */}
          {articleModalOpen && (
            <div className="custom-modal-backdrop">
              <div className="custom-modal-dialog" style={{ maxWidth: '880px' }}>
                <div className="modal-content rounded-4 p-4 shadow-lg bg-white">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="modal-title text-navy fw-bold">
                      {editingArticle ? 'Edit Clinical Article' : 'Publish New Clinical Article'}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setArticleModalOpen(false)}
                    ></button>
                  </div>

                  <form onSubmit={handleSaveArticle}>
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Article Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Modern Advancements in Robotic Knee Replacement"
                        value={articleForm.title}
                        onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                        required
                      />
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Specialist / Sub-field</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Knee Arthroscopy, Sports Medicine"
                          value={articleForm.specialist}
                          onChange={(e) => setArticleForm({ ...articleForm, specialist: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Author</label>
                        <input
                          type="text"
                          className="form-control"
                          value={articleForm.author}
                          onChange={(e) => setArticleForm({ ...articleForm, author: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    {/* Image File Upload Just like Django Admin */}
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">
                        Poster Image File (Upload from Computer)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={handleArticleFileChange}
                      />
                      <small className="text-muted d-block mt-1">
                        Select JPG, PNG or WEBP image file.
                      </small>
                      {articlePreview && (
                        <div className="image-preview-box mt-2 p-2 border rounded bg-light">
                          <span className="small text-muted d-block mb-1">Image Preview:</span>
                          <img
                            src={articlePreview}
                            alt="Poster Preview"
                            style={{ maxHeight: '120px', borderRadius: '6px', objectFit: 'cover' }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Word Document-like Rich Text Editor */}
                    <div className="mb-4">
                      <label className="form-label small fw-semibold d-flex justify-content-between align-items-center mb-1">
                        <span>Article Content (Document Editor) *</span>
                        <span className="text-muted fw-normal small">
                          <i className="fa-solid fa-feather me-1 text-teal"></i> Word-style toolbar with fonts & formatting
                        </span>
                      </label>
                      <RichTextEditor
                        value={articleForm.content}
                        onChange={(contentHtml) => setArticleForm({ ...articleForm, content: contentHtml })}
                        placeholder="Write detailed surgical insights, joint recovery advice, patient guidance..."
                      />
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setArticleModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-admin-primary">
                        {editingArticle ? 'Update Article' : 'Publish Article'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 6: SURGICAL GALLERY */}
      {/* ==================================================================== */}
      {activeTab === 'gallery' && (
        <div className="tab-pane-gallery">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div>
              <h4 className="mb-1 text-navy fw-bold">Surgical & Clinical Gallery</h4>
              <p className="text-muted small mb-0">Manage operating theater photos and YouTube video media</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-excel-export"
                onClick={() => {
                  const exportData = galleryItems.map((g) => ({
                    'Media ID': g.id,
                    'Title': g.title,
                    'Type': g.content_type?.toUpperCase(),
                    'File / URL': g.content_type === 'youtube' ? g.youtube_link : g.photo,
                    'Created At': g.datetime ? format(new Date(g.datetime), 'yyyy-MM-dd') : '',
                  }));
                  exportToExcel(exportData, 'Dr_Sunil_Apsingi_Gallery', 'Gallery');
                  showNotification('Gallery records exported to Excel!');
                }}
                title="Download Gallery Catalog as Excel"
              >
                <i className="fa-solid fa-file-excel text-success me-1"></i> Export to Excel
              </button>
              <button
                className="btn btn-admin-primary d-flex align-items-center gap-2"
                onClick={() => {
                  setGalleryFile(null);
                  setGalleryPreview(null);
                  setGalleryForm({ title: '', content_type: 'photo', youtube_link: '' });
                  setGalleryModalOpen(true);
                }}
              >
                <i className="fa-solid fa-plus"></i>
                <span>Add Media Item</span>
              </button>
            </div>
          </div>

          {galleryLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-orange" role="status"></div>
              <p className="mt-2 text-muted small">Loading surgical gallery...</p>
            </div>
          ) : (
            <div className="row g-4">
              {galleryItems.map((item) => (
                <div key={item.id} className="col-lg-4 col-md-6">
                  <div className="gallery-admin-card bg-white rounded-4 shadow-sm overflow-hidden h-100 d-flex flex-column">
                    <div className="gallery-media-preview position-relative">
                      {item.content_type === 'youtube' && item.youtube_link ? (
                        <div className="ratio ratio-16x9">
                          <iframe
                            src={item.youtube_link.replace('watch?v=', 'embed/')}
                            title={item.title || 'Video'}
                            allowFullScreen
                          ></iframe>
                        </div>
                      ) : (
                        <img
                          src={item.photo || '/images/default.png'}
                          alt={item.title || 'Gallery'}
                          className="gallery-admin-img"
                        />
                      )}
                      <span className="media-type-badge">
                        <i
                          className={`fa-solid ${
                            item.content_type === 'youtube' ? 'fa-video' : 'fa-camera'
                          } me-1`}
                        ></i>
                        {item.content_type}
                      </span>
                    </div>

                    <div className="p-3 mt-auto d-flex justify-content-between align-items-center border-top">
                      <span className="small fw-semibold text-navy text-truncate" style={{ maxWidth: '200px' }}>
                        {item.title || 'Surgical Documentation'}
                      </span>
                      <button
                        className="btn btn-xs btn-outline-danger"
                        onClick={() => handleDeleteGallery(item.id)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Gallery Modal with File Upload / YouTube Toggle */}
          {galleryModalOpen && (
            <div className="custom-modal-backdrop">
              <div className="custom-modal-dialog">
                <div className="modal-content rounded-4 p-4 shadow-lg bg-white">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="modal-title text-navy fw-bold">Add Surgical Media</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setGalleryModalOpen(false)}
                    ></button>
                  </div>

                  <form onSubmit={handleSaveGallery}>
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Media Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Knee Arthroscopy Procedure Demonstration"
                        value={galleryForm.title}
                        onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Media Type *</label>
                      <select
                        className="form-select"
                        value={galleryForm.content_type}
                        onChange={(e) => {
                          setGalleryForm({ ...galleryForm, content_type: e.target.value });
                          setGalleryFile(null);
                          setGalleryPreview(null);
                        }}
                      >
                        <option value="photo">Photo / Operating Theater Image (File Upload)</option>
                        <option value="youtube">YouTube Video (Video Link URL)</option>
                      </select>
                    </div>

                    {/* Conditional Input: Photo File Upload OR YouTube URL */}
                    {galleryForm.content_type === 'photo' ? (
                      <div className="mb-4">
                        <label className="form-label small fw-semibold">
                          Upload Photo File (from Computer) *
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleGalleryFileChange}
                          required
                        />
                        <small className="text-muted d-block mt-1">
                          Upload clear surgical or clinic photograph.
                        </small>
                        {galleryPreview && (
                          <div className="image-preview-box mt-2 p-2 border rounded bg-light">
                            <span className="small text-muted d-block mb-1">Image Preview:</span>
                            <img
                              src={galleryPreview}
                              alt="Gallery Preview"
                              style={{ maxHeight: '140px', borderRadius: '6px', objectFit: 'cover' }}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mb-4">
                        <label className="form-label small fw-semibold">
                          YouTube Video Link (URL) *
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://www.youtube.com/watch?v=..."
                          value={galleryForm.youtube_link}
                          onChange={(e) => setGalleryForm({ ...galleryForm, youtube_link: e.target.value })}
                          required
                        />
                        <small className="text-muted d-block mt-1">
                          Paste YouTube video link. It will automatically embed on the website gallery.
                        </small>
                      </div>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setGalleryModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-admin-primary">
                        Add to Gallery
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 7: REVIEWS & TESTIMONIALS */}
      {/* ==================================================================== */}
      {activeTab === 'reviews' && (
        <div className="tab-pane-reviews">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div>
              <h4 className="mb-1 text-navy fw-bold">Patient Reviews & Feedback</h4>
              <p className="text-muted small mb-0">Total {reviews.length} verified patient testimonials</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-excel-export"
                onClick={handleExportReviews}
                title="Download Patient Reviews as Excel Sheet"
              >
                <i className="fa-solid fa-file-excel text-success me-1"></i> Export to Excel
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchReviews}
                disabled={reviewsLoading}
              >
                <i className={`fa-solid fa-rotate ${reviewsLoading ? 'fa-spin' : ''}`}></i>
              </button>
            </div>
          </div>

          <div className="row g-4">
            {reviews.slice(0, 30).map((rev) => (
              <div key={rev.id} className="col-lg-6">
                <div className="review-admin-card bg-white rounded-4 shadow-sm p-4 h-100 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <strong className="text-navy d-block">{rev.author_name}</strong>
                      <div className="star-rating text-warning small">
                        {'★'.repeat(rev.rating || 5)}
                        <span className="text-muted ms-2 fw-normal">({rev.rating} / 5)</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          checked={rev.is_featured}
                          onChange={() => handleToggleReviewFeatured(rev)}
                          id={`featured-${rev.id}`}
                        />
                        <label className="form-check-label small text-muted" htmlFor={`featured-${rev.id}`}>
                          {rev.is_featured ? 'Featured' : 'Hidden'}
                        </label>
                      </div>
                      <button
                        className="btn btn-xs btn-outline-danger"
                        title="Move review to Recycle Bin"
                        onClick={() => handleDeleteReview(rev.id)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  <p className="small text-secondary mb-3 mt-2 lh-base">
                    "{rev.review_text}"
                  </p>

                  <div className="mt-auto pt-3 border-top">
                    <label className="form-label small fw-semibold text-teal mb-1">
                      <i className="fa-solid fa-reply me-1"></i> Doctor / Clinic Response:
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Write a response from Dr. Sunil..."
                        value={replyDrafts[rev.id] || ''}
                        onChange={(e) =>
                          setReplyDrafts({ ...replyDrafts, [rev.id]: e.target.value })
                        }
                      />
                      <button
                        className="btn btn-sm btn-outline-teal"
                        type="button"
                        onClick={() => handleSaveReviewReply(rev.id)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 8: RECYCLE BIN / SAFE DELETION */}
      {/* ==================================================================== */}
      {activeTab === 'trash' && (
        <div className="tab-pane-trash">
          <div className="filter-toolbar bg-white p-3 rounded-4 shadow-sm mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
            {/* Filter Pills */}
            <div className="filter-pills d-flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'All Deleted Items' },
                { id: 'appointment', label: 'Appointments' },
                { id: 'contact', label: 'Inquiries' },
                { id: 'article', label: 'Articles' },
                { id: 'gallery', label: 'Gallery' },
                { id: 'review', label: 'Reviews' },
              ].map((pill) => (
                <button
                  key={pill.id}
                  className={`btn btn-sm ${trashFilter === pill.id ? 'btn-navy' : 'btn-light'} rounded-pill px-3`}
                  onClick={() => setTrashFilter(pill.id)}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            {/* Search Box */}
            <div className="search-box-wrapper flex-grow-1" style={{ maxWidth: '340px' }}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light border-end-0">
                  <i className="fa-solid fa-magnifying-glass text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search deleted records..."
                  value={trashSearch}
                  onChange={(e) => setTrashSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-excel-export"
                onClick={handleExportTrash}
                title="Download Recycle Bin report as Excel Sheet"
              >
                <i className="fa-solid fa-file-excel text-success me-1"></i> Export to Excel
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchTrash}
                disabled={trashLoading}
              >
                <i className={`fa-solid fa-rotate ${trashLoading ? 'fa-spin' : ''}`}></i>
              </button>
            </div>
          </div>

          {/* Trash Table */}
          <div className="bg-white rounded-4 shadow-sm p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-1 text-navy fw-bold">
                  <i className="fa-solid fa-trash-can text-danger me-2"></i>
                  Recycle Bin ({trashItems.length})
                </h5>
                <small className="text-muted">
                  Safe deletion active. Items remain safely stored here and can be restored back to the live system at any time.
                </small>
              </div>
            </div>

            {trashLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status"></div>
                <p className="mt-2 text-muted small">Loading recycle bin...</p>
              </div>
            ) : trashItems.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fa-regular fa-trash-can fs-1 mb-2 text-muted opacity-50"></i>
                <p className="mb-0 fw-semibold">Recycle Bin is empty.</p>
                <small>No deleted items found matching the current filter.</small>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr className="small text-muted">
                      <th>#</th>
                      <th>Item Type</th>
                      <th>Title / Identification</th>
                      <th>Content Summary</th>
                      <th>Deleted Timestamp</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trashItems.map((item, idx) => (
                      <tr key={`${item.type}-${item.id}`}>
                        <td className="font-monospace text-muted small">{idx + 1}</td>
                        <td>
                          <span className={`badge ${
                            item.type === 'appointment' ? 'bg-primary-subtle text-primary border border-primary-subtle' :
                            item.type === 'contact' ? 'bg-warning-subtle text-warning-emphasis border border-warning-subtle' :
                            item.type === 'article' ? 'bg-info-subtle text-info-emphasis border border-info-subtle' :
                            item.type === 'gallery' ? 'bg-light text-dark border' :
                            'bg-secondary-subtle text-secondary border'
                          } text-uppercase font-monospace px-2 py-1`}>
                            {item.type_label}
                          </span>
                        </td>
                        <td>
                          <strong className="text-navy d-block">{item.title}</strong>
                        </td>
                        <td style={{ maxWidth: '300px' }}>
                          <span className="small text-secondary text-truncate d-block">
                            {item.details || '—'}
                          </span>
                        </td>
                        <td>
                          <span className="small text-muted font-monospace">
                            {item.deleted_at ? format(new Date(item.deleted_at), 'dd MMM yyyy, hh:mm a') : 'N/A'}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="d-inline-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleRestoreTrashItem(item)}
                              title="Restore item back to its original section"
                            >
                              <i className="fa-solid fa-rotate-left me-1"></i> Restore
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handlePurgeTrashItem(item)}
                              title="Delete permanently from database"
                            >
                              <i className="fa-solid fa-fire me-1"></i> Purge
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
